import React from 'react';
import { useAuthStatus } from '../hooks/useAuthStatus';
import { useAuth } from '../context/AuthContext';

/**
 * Conditional rendering component based on authentication status
 * Uses the fast cookie-based check with AuthContext fallback
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render when authenticated
 * @param {React.ReactNode} props.fallback - Content to render when not authenticated
 * @param {React.ReactNode} props.loading - Content to render while checking status
 * @param {boolean} props.inverse - If true, render children when NOT authenticated
 * @returns {React.ReactNode}
 */
export function AuthGuard({ children, fallback = null, loading = null, inverse = false }) {
  const { isAuthenticated: cookieAuth, isLoading: cookieLoading } = useAuthStatus();
  const { user, loading: contextLoading } = useAuth();

  // Use cookie auth if available, otherwise fall back to context auth
  const isAuthenticated = cookieAuth || !!user;
  const isLoading = cookieLoading || contextLoading;

  if (isLoading) {
    return loading;
  }

  if (inverse) {
    return isAuthenticated ? fallback : children;
  }

  return isAuthenticated ? children : fallback;
}

/**
 * Component that renders content only for authenticated users
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render for authenticated users
 * @param {React.ReactNode} props.fallback - Optional fallback content for guests
 * @returns {React.ReactNode}
 */
export function AuthenticatedOnly({ children, fallback = null }) {
  return (
    <AuthGuard fallback={fallback}>
      {children}
    </AuthGuard>
  );
}

/**
 * Component that renders content only for guest users (not authenticated)
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render for guest users
 * @param {React.ReactNode} props.fallback - Optional fallback content for authenticated users
 * @returns {React.ReactNode}
 */
export function GuestOnly({ children, fallback = null }) {
  return (
    <AuthGuard inverse fallback={fallback}>
      {children}
    </AuthGuard>
  );
}

export default AuthGuard;
